<script setup lang="ts">
import { promiseTimeout } from '@vueuse/core'
import LineXClose from '@docue/docue-ui-v2/icons/LineXClose.vue'
import LineThumbsUp from '@docue/docue-ui-v2/icons/LineThumbsUp.vue'
import LineThumbsDown from '@docue/docue-ui-v2/icons/LineThumbsDown.vue'
import {
  PopoverAnchor,
  PopoverContent,
  PopoverRoot,
} from 'reka-ui'

defineOptions({
  inheritAttrs: false,
})

const props = defineProps<{
  messageId: string
  chatId?: string
}>()

const emit = defineEmits<{
  feedbackSubmitted: []
}>()

const { t } = useI18n()
const { $posthog } = useNuxtApp()
const isOpen = ref(false)

const selectedFeedback = ref<'0' | '1' | undefined>(undefined)
const message = ref('')

whenever(selectedFeedback, () => {
  $posthog.capture('chat_message_feedback_selected', {
    message_id: props.messageId,
    trace_id: props.chatId,
    feedback_value: selectedFeedback.value === '1' ? 1 : -1,
  })

  isOpen.value = true
})

const isSubmitEnabled = computed(() => !!selectedFeedback.value && !!message.value.trim().length)

const { execute: submit, isLoading: isSubmitting } = useAsyncState(async () => {
  if (!isSubmitEnabled.value)
    return

  $posthog.capture('chat_message_feedback_submitted', {
    message_id: props.messageId,
    trace_id: props.chatId,
    feedback_message: message.value.trim(),
    feedback_value: selectedFeedback.value === '1' ? 1 : -1,
  })

  // Timeout is to have a loading state, otherwise the popover will close immediately.
  await promiseTimeout(300)
  emit('feedbackSubmitted')

  isOpen.value = false
  message.value = ''
}, undefined, { immediate: false })
</script>

<template>
  <PopoverRoot
    :open="isOpen"
    @update:open="(open) => {
      isOpen = open
      if (!open) {
        message = ''
      }
    }"
  >
    <PopoverAnchor as-child>
      <div
        v-bind="$attrs"
        class="flex w-fit items-center gap-1"
      >
        <label class="flex size-6 items-center justify-center rounded-full hover:bg-gray-100 has-[:checked]:bg-gray-100">
          <input
            v-model="selectedFeedback"
            type="radio"
            name="feedback"
            value="1"
            class="sr-only"
          >
          <LineThumbsUp class="size-3 shrink-0" />
        </label>
        <label class="flex size-6 items-center justify-center rounded-full hover:bg-gray-100 has-[:checked]:bg-gray-100">
          <input
            v-model="selectedFeedback"
            type="radio"
            name="feedback"
            value="0"
            class="sr-only"
          >
          <LineThumbsDown class="size-3 shrink-0" />
        </label>
      </div>
    </PopoverAnchor>

    <PopoverContent
      side="top"
      :side-offset="8"
      as-child
    >
      <form
        class="flex flex-col rounded-xl border border-gray-100 bg-white drop-shadow"
        @submit.prevent="() => submit()"
      >
        <div class="relative p-6">
          <DTIconButton
            variant="plain"
            size="sm"
            class="absolute left-[22px] top-[22px]"
            :disabled="isSubmitting"
            @click="isOpen = false"
          >
            <LineXClose class="size-4" />
          </DTIconButton>
          <p class="w-full px-6 text-center text-sm font-semibold">
            {{ t('aiChat.feedback.title') }}
          </p>
        </div>
        <div class="px-6">
          <DTField :label="t('aiChat.feedback.message')">
            <DTFieldTextarea
              v-model="message"
              :placeholder="t('aiChat.feedback.placeholder')"
              rows="3"
              class="min-h-16 text-sm"
              wrapper-class="mt-1.5"
              :disabled="isSubmitting"
            />
          </DTField>
        </div>

        <div class="flex items-center justify-end gap-2 p-6 pt-4">
          <DTButton
            size="sm"
            @click="isOpen = false"
          >
            {{ t('close') }}
          </DTButton>
          <DTButton
            variant="blue"
            size="sm"
            :disabled="isSubmitting || !isSubmitEnabled"
            type="submit"
          >
            {{ t('submit') }}
            <DTSpinner
              v-if="isSubmitting"
              variant="white"
              :size="4"
              :weight="2"
            />
          </DTButton>
        </div>
      </form>
    </PopoverContent>
  </PopoverRoot>
</template>
